import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    X, ChevronLeft, ChevronRight, Loader2, Download, ExternalLink, Printer, CloudUpload, PanelRightClose, PanelRightOpen,
    CheckCircle2, FileArchive, Lock, ArrowLeft, File as FileIcon, PencilLine
} from "@fg/icons";
import { findApp, type SuiteAppId } from "@/features/shell/suiteApps";
import type { Entry as ZipEntry } from "@zip.js/zip.js";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useFocusTrap } from "@/lib/useFocusTrap";
import { useDismiss } from "@/lib/useDismiss";
import { driveApi, driveOpenUrl, isDriveConfigured, MAIL_ATTACHMENTS_FOLDER } from "@/api/driveApi";
import { DriveFilePicker } from "./DriveFilePicker";
import { extensionOf, previewKind } from "./quickView";

// The PdfViewer bundle is large (~1 MB gzipped) and only used for previews,
// so lazy-load it so the rest of mail-web isn't slowed down.
const PdfViewerHost = lazy(() =>
    import("./PdfViewerHost").then((m) => ({ default: m.PdfViewerHost }))
);

// Word and Excel are drawn by the Notes and Workbook engines (a wasm module each), loaded only when one is opened.
const WordViewerHost = lazy(() => import("./OfficeViewerHost").then((m) => ({ default: m.WordViewerHost })));
const SheetViewerHost = lazy(() => import("./OfficeViewerHost").then((m) => ({ default: m.SheetViewerHost })));
const PRINT_EVENT = "fg-preview-print";

const driveEnabled = isDriveConfigured();
const DRIVE_BFF = (import.meta.env.VITE_DRIVE_BFF_BASE_URL ?? "").replace(/\/+$/, "");

/** Which TribeX app opens a file (user: txt/doc/docx → Docs, csv/Excel → Sheets, PowerPoint → Slides, PDF → PDF). */
const APP_BY_EXT: Record<string, SuiteAppId> = {
    txt: "docs", doc: "docs", docx: "docs", rtf: "docs", odt: "docs",
    csv: "sheet", xls: "sheet", xlsx: "sheet", xlsm: "sheet", ods: "sheet",
    ppt: "present", pptx: "present", odp: "present",
    pdf: "pdfViewer"
};

function appFor(fileName: string): { id: SuiteAppId; name: string; url: string } | null {
    const id = APP_BY_EXT[extensionOf(fileName)];
    const app = id ? findApp(id) : undefined;
    if (!id || !app || (app.comingSoon && id !== "pdfViewer")) return null;
    return { id, name: app.name.replace(/^TribeX\s+/, ""), url: app.url };
}

/** Text previews stop here — a log file can be huge. */
const MAX_TEXT_CHARS = 2_000_000;
/** CSV / TSV previews show the first rows only. */
const MAX_TABLE_ROWS = 1000;
/** Files inside a zip bigger than this aren't unpacked for a preview. */
const MAX_ZIP_ENTRY_BYTES = 50 * 1024 * 1024;
/** A preview service call gives up after this (the services themselves stop at 20 s). */
const PREVIEW_TIMEOUT_MS = 30_000;
const TOO_LONG = "This file took too long to preview — download it to open.";

interface PdfPreviewDialogProps {
    open: boolean;
    fileName: string;
    /** Bearer-authenticated URL the dialog fetches the file from. */
    downloadUrl: string;
    accessToken: string | undefined;
    onClose: () => void;
    /** Gallery navigation across a message's previewable attachments. */
    onPrev?: () => void;
    onNext?: () => void;
    /** 1-based position label, e.g. "2 / 5". Hidden when there's only one. */
    position?: { index: number; total: number };
    /** Side pane beside the file — the message, in the list's quick view (Outlook). */
    aside?: React.ReactNode;
}

/** One sheet from the Sheet service's /api/sheet/Preview — rows of display text from A1. */
interface SheetPreview {
    name: string;
    rows: string[][];
    truncated?: boolean;
}

interface ZipItem {
    index: number;
    path: string;
    name: string;
    folder: string;
    size: number;
    date: Date | null;
    encrypted: boolean;
}

/** A file made ready to show. `url` is what Print and "open in a tab" use. */
type Rendered =
    // `blob` is the same file as `url`: the PDF viewer reads it directly, because the page CSP does not let it fetch a blob: URL.
    | { kind: "pdf" | "office" | "image"; url: string; blob: Blob }
    | { kind: "word"; url: string; html: string }
    // Shown by the Notes / Workbook engine; `fallback` makes the service-rendered version if the engine can't open the file.
    | { kind: "docx"; blob: Blob; fallback: () => Promise<Rendered> }
    | { kind: "xlsx"; blob: Blob; fallback: () => Promise<Rendered> }
    | { kind: "text"; url: string; text: string }
    | { kind: "sheet"; url: string; sheets: SheetPreview[] }
    | { kind: "zip"; items: ZipItem[] };

/** A file opened from inside a zip. */
interface Inner {
    name: string;
    original: Blob | null;
    view: Rendered | null;
    loading: boolean;
    error: string | null;
}

/**
 * Attachment preview (Outlook's attachment viewer): the file large on the
 * left, optionally the message on the right; Download, Print and Save to
 * Drive on top. What it shows depends on the type (quickView.ts): PDF in the
 * PDF viewer (which asks for a protected PDF's password); Excel as a grid;
 * Word / PowerPoint converted by the Office services' /ToPdf; images directly;
 * text as a page and CSV / TSV as a table; a zip as the list of its files,
 * each opening here too (a protected zip asks for its password — unpacked in
 * the browser). Download and Save to Drive use the ORIGINAL file, Print what's
 * on screen.
 */
export function PdfPreviewDialog({ open, fileName, downloadUrl, accessToken, onClose, onPrev, onNext, position, aside }: PdfPreviewDialogProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const hasGallery = Boolean(position && position.total > 1);
    const kind = previewKind(fileName) ?? "pdf";
    const [view, setView] = useState<Rendered | null>(null);
    const originalRef = useRef<Blob | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [showAside, setShowAside] = useState(true);
    const [pickingFolder, setPickingFolder] = useState(false);
    const [drive, setDrive] = useState<{ state: "saving" | "saved" | "failed"; text: string; href?: string } | null>(null);
    const [handoff, setHandoff] = useState<{ state: "working" | "done" | "failed"; text: string } | null>(null);

    // Zip: the entries (kept for unpacking), the file opened from it, and the password once it worked.
    const zipEntriesRef = useRef<ZipEntry[] | null>(null);
    const zipPasswordRef = useRef("");
    const [inner, setInner] = useState<Inner | null>(null);
    const [askPassword, setAskPassword] = useState<{ item: ZipItem; error: string | null } | null>(null);

    // The Drive folder picker is its own dialog (a portal): while it's open,
    // clicks inside it must not count as "outside" and close the preview.
    useDismiss(containerRef, onClose, open && !pickingFolder);
    useFocusTrap(containerRef, open && !pickingFolder);

    // Prevent background scroll while the modal is open.
    useEffect(() => {
        if (!open) return;
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previous;
        };
    }, [open]);

    // ← / → step through the message's files (Esc is handled by useDismiss).
    useEffect(() => {
        if (!open || !hasGallery) return;
        const onKey = (e: KeyboardEvent) => {
            if (pickingFolder || (e.target instanceof HTMLElement && e.target.closest("input, textarea"))) return;
            if (e.key === "ArrowLeft") onPrev?.();
            if (e.key === "ArrowRight") onNext?.();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, hasGallery, onPrev, onNext, pickingFolder]);

    useEffect(() => {
        if (!open || !downloadUrl) return;
        let cancelled = false;
        let made: Rendered | null = null;
        setDrive(null);
        setHandoff(null);
        setView(null);
        setInner(null);
        setAskPassword(null);
        zipEntriesRef.current = null;
        zipPasswordRef.current = "";
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const headers = authHeaders(accessToken);
                const res = await fetch(downloadUrl, { headers, credentials: "omit" });
                if (!res.ok) {
                    throw new Error(`The file couldn't be fetched (${res.status} ${res.statusText}).`);
                }
                const original = await res.blob();
                if (cancelled) return;
                originalRef.current = original;
                let rendered: Rendered;
                if (kind === "zip") {
                    const zip = await openZip(original);
                    if (cancelled) return;
                    zipEntriesRef.current = zip.entries;
                    rendered = { kind: "zip", items: zip.items };
                } else {
                    rendered = await renderFile(original, fileName, headers);
                }
                if (cancelled) {
                    revoke(rendered);
                    return;
                }
                made = rendered;
                setView(rendered);
            } catch (err) {
                if (cancelled) return;
                setError(message(err));
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        void load();
        return () => {
            cancelled = true;
            if (made) revoke(made);
            originalRef.current = null;
            setView(null);
        };
    }, [open, downloadUrl, accessToken, fileName, kind]);

    // A file opened from the zip releases its blob URL when it's closed or replaced.
    useEffect(() => () => {
        if (inner?.view) revoke(inner.view);
    }, [inner]);

    if (!open) return null;

    /** Opens a file from the zip here — after the password, when the zip has one. */
    const openZipItem = async (item: ZipItem, password = zipPasswordRef.current) => {
        if (item.encrypted && !password) {
            setAskPassword({ item, error: null });
            return;
        }
        if (item.size > MAX_ZIP_ENTRY_BYTES) {
            setInner({ name: item.name, original: null, view: null, loading: false, error: "This file is too large to preview — download the zip to open it." });
            return;
        }
        const entry = zipEntriesRef.current?.[item.index];
        if (!entry || entry.directory || typeof entry.getData !== "function") return;
        setInner({ name: item.name, original: null, view: null, loading: true, error: null });
        let blob: Blob;
        try {
            const zipjs = await import("@zip.js/zip.js");
            blob = await entry.getData(new zipjs.BlobWriter(), password ? { password } : undefined);
        } catch (err) {
            const text = message(err);
            if (/password|encrypt/i.test(text)) {
                // Wrong (or missing) password: back to the list, ask again.
                zipPasswordRef.current = "";
                setInner(null);
                setAskPassword({ item, error: password ? "That password isn't right — try again." : null });
            } else {
                setInner({ name: item.name, original: null, view: null, loading: false, error: `This file couldn't be unpacked (${text}).` });
            }
            return;
        }
        if (password) zipPasswordRef.current = password;
        setAskPassword(null);
        if (previewKind(item.name) === "zip") {
            setInner({ name: item.name, original: blob, view: null, loading: false, error: "A zip inside the zip can't be previewed — download it to open." });
            return;
        }
        try {
            const rendered = await renderFile(blob, item.name, authHeaders(accessToken));
            setInner({ name: item.name, original: blob, view: rendered, loading: false, error: null });
        } catch (err) {
            setInner({ name: item.name, original: blob, view: null, loading: false, error: message(err) });
        }
    };

    // What's on screen — a file from the zip, or the attachment itself.
    const shown = inner
        ? { name: inner.name, original: inner.original, view: inner.view, loading: inner.loading, error: inner.error }
        : { name: fileName, original: originalRef.current, view, loading, error };
    const printable = shown.view && "url" in shown.view ? shown.view.url : null;
    const nativePrint = shown.view?.kind === "docx" || shown.view?.kind === "xlsx";
    // Previews are read-only. "Open in Docs / Sheets / Slides / PDF" copies the
    // file to the user's Drive (Mail Attachments) and opens that copy in its
    // app, which saves edits back to Drive — as Outlook does with OneDrive.
    // The apps only send a token to Drive, so a Mail attachment link can't be
    // handed over directly.
    const app = shown.original && driveEnabled ? appFor(shown.name) : null;
    const openInApp = async () => {
        const original = shown.original;
        const name = shown.name;
        if (!original || !app) return;
        // Open the tab now: one opened after the upload would be blocked as a pop-up.
        const tab = window.open("about:blank", "_blank");
        setHandoff({ state: "working", text: `Opening in ${app.name}…` });
        try {
            const folder = await driveApi.mailAttachmentsFolder(accessToken);
            const item = await driveApi.uploadFile(new File([original], name, { type: original.type }), folder.id, accessToken, () => undefined);
            const url = new URL(app.url);
            url.searchParams.set("fileId", item.id);
            if (app.id !== "pdfViewer") {
                url.searchParams.set("fileName", item.name);
                url.searchParams.set("openFile", `${DRIVE_BFF}/api/files/${encodeURIComponent(item.id)}/download`);
            }
            if (tab) {
                tab.opener = null;
                tab.location.href = url.toString();
            } else {
                window.open(url.toString(), "_blank", "noopener,noreferrer");
            }
            setHandoff({ state: "done", text: `Opened in ${app.name} · a copy is in Drive › ${MAIL_ATTACHMENTS_FOLDER}` });
        } catch (err) {
            tab?.close();
            setHandoff({ state: "failed", text: `Couldn't open in ${app.name} — ${message(err)}` });
        }
    };

    const handleDownload = () => {
        const original = shown.original;
        if (!original) return;
        const url = URL.createObjectURL(original);
        const a = document.createElement("a");
        a.href = url;
        a.download = shown.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 10_000);
    };

    // Prints what's on screen from a hidden frame (the mail CSP allows blob: frames).
    const handlePrint = () => {
        // A file shown by the Word / Excel engine prints itself.
        if (nativePrint) { window.dispatchEvent(new Event(PRINT_EVENT)); return; }
        if (!printable) return;
        const frame = document.createElement("iframe");
        frame.setAttribute("aria-hidden", "true");
        frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
        frame.src = printable;
        frame.onload = () => {
            try {
                frame.contentWindow?.focus();
                frame.contentWindow?.print();
            } catch {
                window.open(printable, "_blank", "noopener,noreferrer");
            }
            setTimeout(() => frame.remove(), 60_000);
        };
        document.body.appendChild(frame);
    };

    const saveToDrive = async (folder: { id: string | null; name: string }) => {
        const original = shown.original;
        const name = shown.name;
        if (!original) return;
        setDrive({ state: "saving", text: "Saving to Drive…" });
        try {
            const item = await driveApi.uploadFile(
                new File([original], name, { type: original.type }),
                folder.id,
                accessToken,
                (fraction) => setDrive({ state: "saving", text: `Saving to Drive… ${Math.floor(fraction * 100)}%` })
            );
            setDrive({ state: "saved", text: `Saved to ${folder.name}`, href: driveOpenUrl(item.id) });
        } catch (err) {
            setDrive({ state: "failed", text: `Save to Drive failed — ${message(err)}` });
        }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={`Preview ${fileName}`}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-3 sm:p-5"
        >
            <div
                ref={containerRef}
                className="flex h-full max-h-[94vh] w-full max-w-[1600px] flex-col overflow-hidden rounded-lg bg-card shadow-[var(--mail-shadow-28)]"
            >
                <header className="flex h-12 shrink-0 items-center gap-1 border-b border-[var(--mail-line)] px-2 sm:px-3">
                    {hasGallery ? (
                        <div className="flex shrink-0 items-center">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onPrev} title="Previous attachment (←)" aria-label="Previous attachment">
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <span className="text-xs tabular-nums text-[var(--mail-text-2)]">{position!.index} / {position!.total}</span>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onNext} title="Next attachment (→)" aria-label="Next attachment">
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    ) : null}
                    <div className="flex min-w-0 max-w-[45%] items-center gap-1 px-2 text-sm font-semibold">
                        {inner ? (
                            <>
                                <button
                                    type="button"
                                    onClick={() => setInner(null)}
                                    title={`Back to ${fileName}`}
                                    className="inline-flex min-w-0 shrink items-center gap-1 rounded text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                >
                                    <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
                                    <span className="truncate">{fileName}</span>
                                </button>
                                <span className="shrink-0 text-[var(--mail-text-3)]" aria-hidden>›</span>
                                <span className="truncate" title={inner.name}>{inner.name}</span>
                            </>
                        ) : (
                            <span className="truncate" title={fileName}>{fileName}</span>
                        )}
                    </div>
                    <div className="flex min-w-0 items-center gap-0.5 overflow-x-auto [scrollbar-width:none]">
                        <HeaderAction icon={<Download className="h-4 w-4" />} label="Download" disabled={!shown.original} onClick={handleDownload} />
                        <HeaderAction icon={<Printer className="h-4 w-4" />} label="Print" disabled={!printable && !nativePrint} onClick={handlePrint} />
                        {driveEnabled ? (
                            <HeaderAction
                                icon={<CloudUpload className="h-4 w-4" />}
                                label="Save to Drive"
                                disabled={!shown.original || drive?.state === "saving"}
                                onClick={() => setPickingFolder(true)}
                            />
                        ) : null}
                        {app ? (
                            <HeaderAction
                                icon={<PencilLine className="h-4 w-4" />}
                                label={`Open in ${app.name}`}
                                disabled={handoff?.state === "working"}
                                onClick={() => void openInApp()}
                            />
                        ) : null}
                        {handoff ? (
                            <span
                                role="status"
                                className={
                                    "ml-1 inline-flex items-center gap-1.5 whitespace-nowrap text-xs " +
                                    (handoff.state === "failed" ? "text-destructive" : "text-[var(--mail-text-2)]")
                                }
                            >
                                {handoff.state === "working" ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
                                {handoff.state === "done" ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" aria-hidden /> : null}
                                {handoff.text}
                            </span>
                        ) : null}
                        {drive ? (
                            <span
                                role="status"
                                className={
                                    "ml-1 inline-flex items-center gap-1.5 whitespace-nowrap text-xs " +
                                    (drive.state === "failed" ? "text-destructive" : "text-[var(--mail-text-2)]")
                                }
                            >
                                {drive.state === "saving" ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
                                {drive.state === "saved" ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" aria-hidden /> : null}
                                {drive.text}
                                {drive.href ? (
                                    <a href={drive.href} target="_blank" rel="noopener noreferrer" className="font-semibold text-primary hover:underline">
                                        Open in Drive
                                    </a>
                                ) : null}
                            </span>
                        ) : null}
                    </div>
                    <div className="ml-auto flex shrink-0 items-center gap-0.5">
                        {aside ? (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="hidden h-8 w-8 md:inline-flex"
                                onClick={() => setShowAside((v) => !v)}
                                aria-pressed={showAside}
                                title={showAside ? "Hide the message" : "Show the message"}
                                aria-label={showAside ? "Hide the message" : "Show the message"}
                            >
                                {showAside ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
                            </Button>
                        ) : null}
                        {/* Documents open in their TribeX app (above); only an image
                            has nothing better than a plain tab. */}
                        {shown.view?.kind === "image" && printable ? (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => window.open(printable, "_blank", "noopener,noreferrer")}
                                title="Open the image in a new tab"
                                aria-label="Open the image in a new tab"
                            >
                                <ExternalLink className="h-4 w-4" />
                            </Button>
                        ) : null}
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} title="Close (Esc)" aria-label="Close preview">
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                </header>

                <div className="flex min-h-0 flex-1">
                    <div className="relative min-h-0 min-w-0 flex-1 bg-[var(--mail-subtle)]">
                        {shown.loading ? (
                            <div className="flex h-full items-center justify-center text-sm text-[var(--mail-text-2)]">
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {inner ? "Opening…" : "Loading preview…"}
                            </div>
                        ) : shown.error ? (
                            <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center text-sm">
                                <span className="font-semibold text-foreground">No preview for this file</span>
                                <span className="max-w-md text-xs text-[var(--mail-text-2)]">{shown.error}</span>
                                {shown.original ? (
                                    <Button size="sm" onClick={handleDownload} className="gap-1.5">
                                        <Download className="h-4 w-4" /> Download instead
                                    </Button>
                                ) : null}
                            </div>
                        ) : !shown.view ? null : shown.view.kind === "zip" ? (
                            <ZipList
                                items={shown.view.items}
                                askPassword={askPassword}
                                onOpen={(item) => void openZipItem(item)}
                                onPassword={(item, password) => void openZipItem(item, password)}
                                onCancelPassword={() => setAskPassword(null)}
                            />
                        ) : (
                            <FileView view={shown.view} name={shown.name} />
                        )}
                    </div>
                    {aside && showAside ? (
                        <aside
                            aria-label="Message"
                            className="hidden w-[380px] shrink-0 overflow-y-auto border-l border-[var(--mail-line)] bg-[var(--mail-pane)] md:block"
                        >
                            {aside}
                        </aside>
                    ) : null}
                </div>
            </div>
            {driveEnabled ? (
                <DriveFilePicker
                    mode="folder"
                    open={pickingFolder}
                    onOpenChange={(o) => setPickingFolder(o)}
                    title="Save to Drive"
                    description={`Choose where to save ${shown.name}.`}
                    onPickFolder={(folder) => {
                        setPickingFolder(false);
                        void saveToDrive(folder);
                    }}
                />
            ) : null}
        </div>
    );
}

function authHeaders(accessToken: string | undefined): Headers {
    const headers = new Headers();
    if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
    return headers;
}

function message(err: unknown): string {
    return err instanceof Error ? err.message : String(err);
}

function revoke(view: Rendered) {
    if ("url" in view) URL.revokeObjectURL(view.url);
}

/** Turns one file into something to show — the same path for attachments and files inside a zip. */
async function renderFile(original: Blob, fileName: string, headers: Headers, native = true): Promise<Rendered> {
    const kind = previewKind(fileName);
    if (!kind || kind === "zip") throw new Error("There's no preview for this type of file.");
    if (kind === "pdf" || kind === "image") {
        // Image blobs from a zip carry no type; the browser sniffs them, but a PDF needs one.
        const typed = kind === "pdf" && !original.type ? new Blob([original], { type: "application/pdf" }) : original;
        return { kind, url: URL.createObjectURL(typed), blob: typed };
    }
    if (kind === "text") {
        const text = (await original.text()).slice(0, MAX_TEXT_CHARS);
        return { kind, text, url: URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" })) };
    }

    // .docx and .xlsx / .xlsm are drawn by TribeX's own engines; older formats (.doc, .rtf, .xls) and anything the
    // engines cannot open go through the Office services as before.
    const ext = extensionOf(fileName);
    if (native && ((kind === "word" && ext === "docx") || (kind === "sheet" && (ext === "xlsx" || ext === "xlsm")))) {
        return { kind: kind === "word" ? "docx" : "xlsx", blob: original, fallback: () => renderFile(original, fileName, headers, false) };
    }

    const form = new FormData();
    form.append("files", new File([original], fileName, { type: original.type }));
    const call = async (url: string): Promise<Response> => {
        let res: Response;
        try {
            res = await fetch(url, { method: "POST", body: form, headers, credentials: "omit", signal: AbortSignal.timeout(PREVIEW_TIMEOUT_MS) });
        } catch (err) {
            if (err instanceof DOMException && (err.name === "TimeoutError" || err.name === "AbortError")) throw new Error(TOO_LONG);
            throw new Error("The preview service couldn't be reached.");
        }
        if (res.status === 504) throw new Error(TOO_LONG);
        return res;
    };

    if (kind === "sheet") {
        // Excel as an Excel-like grid (Outlook): the Sheet service returns each
        // sheet's cells as text; Print and "open in a tab" use the same cells.
        const base = import.meta.env.VITE_SHEET_SERVICE_URL as string | undefined;
        if (!base) throw new Error("Previews of this file type aren't set up here.");
        const res = await call(`${base.replace(/\/$/, "")}/Preview`);
        if (!res.ok) throw new Error(`The preview service couldn't read this workbook (${res.status}).`);
        const data = (await res.json()) as { sheets?: SheetPreview[] };
        const sheets = (data.sheets ?? []).filter((s) => Array.isArray(s.rows));
        return { kind, sheets, url: URL.createObjectURL(new Blob([sheetsToHtml(fileName, sheets)], { type: "text/html;charset=utf-8" })) };
    }

    if (kind === "word") {
        // Word opens as the document itself, read-only (not a PDF): the Docs
        // service's HTML export, shown in a sandboxed frame.
        const docsBase = import.meta.env.VITE_DOCS_SERVICE_URL as string | undefined;
        if (!docsBase) throw new Error("Previews of this file type aren't set up here.");
        const res = await call(`${docsBase.replace(/\/$/, "")}/Preview`);
        if (!res.ok) throw new Error(`The preview service couldn't read this document (${res.status}). If it's password-protected, download it to open.`);
        const html = wordPageHtml(await res.text());
        return { kind, html, url: URL.createObjectURL(new Blob([html], { type: "text/html;charset=utf-8" })) };
    }

    // PowerPoint → the Slides service's /ToPdf, so the PDF viewer shows the slides.
    const base = import.meta.env.VITE_PRESENT_SERVICE_URL as string | undefined;
    if (!base) throw new Error("Previews of this file type aren't set up here.");
    const res = await call(`${base.replace(/\/$/, "")}/ToPdf`);
    if (!res.ok) throw new Error(`The preview service couldn't convert this file (${res.status}). If it's password-protected, download it to open.`);
    const converted = await res.blob();
    return { kind: "office", url: URL.createObjectURL(converted), blob: converted };
}

/** Reads a zip's directory in the browser (zip.js, loaded on first use). */
async function openZip(blob: Blob): Promise<{ entries: ZipEntry[]; items: ZipItem[] }> {
    const zipjs = await import("@zip.js/zip.js");
    zipjs.configure({ useWebWorkers: false });
    const reader = new zipjs.ZipReader(new zipjs.BlobReader(blob));
    let entries: ZipEntry[];
    try {
        entries = await reader.getEntries();
    } catch (err) {
        throw new Error(`This zip couldn't be read (${message(err)}).`);
    }
    const items = entries
        .map((e, index) => ({ e, index }))
        .filter(({ e }) => !e.directory)
        .map(({ e, index }): ZipItem => {
            const parts = e.filename.split("/").filter(Boolean);
            return {
                index,
                path: e.filename,
                name: parts[parts.length - 1] ?? e.filename,
                folder: parts.slice(0, -1).join("/"),
                size: e.uncompressedSize,
                date: e.lastModDate instanceof Date && !Number.isNaN(e.lastModDate.getTime()) ? e.lastModDate : null,
                encrypted: Boolean(e.encrypted)
            };
        })
        .sort((a, b) => a.path.localeCompare(b.path));
    return { entries, items };
}

/** What a rendered (non-zip) file looks like on screen. */
function FileView({ view, name }: { view: Exclude<Rendered, { kind: "zip" }>; name: string }) {
    if (view.kind === "image") {
        return (
            <div className="absolute inset-0 flex items-center justify-center overflow-auto p-4">
                <img src={view.url} alt={name} className="max-h-full max-w-full rounded bg-white object-contain shadow-[var(--mail-shadow-8)]" />
            </div>
        );
    }
    if (view.kind === "docx" || view.kind === "xlsx") return <NativeOffice view={view} name={name} />;
    if (view.kind === "word") return <DocFrame html={view.html} name={name} />;
    if (view.kind === "text") return <TextPreview fileName={name} text={view.text} />;
    if (view.kind === "sheet") return <SheetGrid key={view.url} sheets={view.sheets} />;
    return (
        <div className="absolute inset-0">
            <Suspense fallback={
                <div className="flex h-full items-center justify-center text-sm text-[var(--mail-text-2)]">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading viewer…
                </div>
            }>
                <PdfViewerHost key={view.url} blob={view.blob} />
            </Suspense>
        </div>
    );
}

/** A Word / Excel file in the new engines' viewer; if the engine cannot open it, the service-made preview takes over. */
function NativeOffice({ view, name }: { view: Extract<Rendered, { kind: "docx" | "xlsx" }>; name: string }) {
    const [alt, setAlt] = useState<Exclude<Rendered, { kind: "zip" }> | null>(null);
    const [problem, setProblem] = useState<string | null>(null);
    const [failed, setFailed] = useState(false);
    const altRef = useRef<Rendered | null>(null);
    useEffect(() => () => { if (altRef.current) revoke(altRef.current); }, []);
    const fail = useCallback(() => {
        setFailed((was) => {
            if (!was) {
                void view.fallback()
                    .then((r) => { if (r.kind === "zip") return; altRef.current = r; setAlt(r); })
                    .catch((e) => setProblem(message(e)));
            }
            return true;
        });
    }, [view]);
    if (alt) return <FileView view={alt} name={name} />;
    if (problem) return <p className="absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-[var(--mail-text-2)]">{problem}</p>;
    if (failed) return <div className="absolute inset-0 flex items-center justify-center text-sm text-[var(--mail-text-2)]"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading preview…</div>;
    const Host = view.kind === "docx" ? WordViewerHost : SheetViewerHost;
    return (
        <div className="absolute inset-0">
            <Suspense fallback={<div className="flex h-full items-center justify-center text-sm text-[var(--mail-text-2)]"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading viewer…</div>}>
                <Host blob={view.blob} onFail={fail} />
            </Suspense>
        </div>
    );
}

/**
 * A Word document as a read-only page. The frame is fully sandboxed (no
 * scripts, no same-origin) and the document's own CSP blocks every remote
 * load — only its inlined images show. Links open in a new tab.
 */
function DocFrame({ html, name }: { html: string; name: string }) {
    return (
        <iframe
            title={name}
            srcDoc={html}
            sandbox="allow-popups allow-popups-to-escape-sandbox"
            className="absolute inset-0 h-full w-full border-0 bg-[#eef1f5]"
        />
    );
}

/** DocIO's HTML plus our frame rules: no remote loads, the document on a white page. */
function wordPageHtml(html: string): string {
    const head =
        `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data: blob:; style-src 'unsafe-inline'; font-src data:">`
        + `<meta name="referrer" content="no-referrer"><base target="_blank">`
        + `<style>html{background:#eef1f5}body{background:#fff;color:#1b1f24;max-width:816px;margin:24px auto !important;`
        + `padding:56px 64px !important;box-sizing:border-box;min-height:calc(100vh - 48px);`
        + `box-shadow:0 0 2px rgba(0,0,0,.12),0 2px 4px rgba(0,0,0,.14)}img{max-width:100%;height:auto}table{max-width:100%}</style>`;
    return /<head[^>]*>/i.test(html)
        ? html.replace(/<head[^>]*>/i, (m) => m + head)
        : `<!doctype html><html><head><meta charset="utf-8">${head}</head><body>${html}</body></html>`;
}

/** The files inside a zip; a protected zip asks for its password above the list. */
function ZipList({
    items, askPassword, onOpen, onPassword, onCancelPassword
}: {
    items: ZipItem[];
    askPassword: { item: ZipItem; error: string | null } | null;
    onOpen: (item: ZipItem) => void;
    onPassword: (item: ZipItem, password: string) => void;
    onCancelPassword: () => void;
}) {
    const [password, setPassword] = useState("");
    const total = items.reduce((sum, i) => sum + i.size, 0);
    const locked = items.some((i) => i.encrypted);
    return (
        <div className="absolute inset-0 flex flex-col bg-card">
            {askPassword ? (
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (password) onPassword(askPassword.item, password);
                    }}
                    className="flex flex-wrap items-center gap-2 border-b border-[var(--mail-warn-border)] bg-[var(--mail-warn-bg)] px-4 py-3 text-[13px] text-[var(--mail-warn-fg)]"
                >
                    <Lock className="h-4 w-4 shrink-0" aria-hidden />
                    <label htmlFor="zip-password" className="min-w-0">
                        This zip is password-protected. Enter the password to open “{askPassword.item.name}”.
                    </label>
                    <input
                        id="zip-password"
                        type="password"
                        autoFocus
                        autoComplete="off"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="h-8 w-56 rounded border border-[var(--mail-line-strong)] bg-card px-2 text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                    <Button type="submit" size="sm" disabled={!password}>Open</Button>
                    <Button type="button" size="sm" variant="ghost" onClick={onCancelPassword}>Cancel</Button>
                    {askPassword.error ? <span role="alert" className="w-full text-xs font-semibold text-destructive">{askPassword.error}</span> : null}
                    <span className="w-full text-[11px] opacity-80">The zip is opened in your browser — the password isn't sent anywhere.</span>
                </form>
            ) : null}
            <div className="flex shrink-0 items-center gap-2 border-b border-[var(--mail-line)] px-4 py-2 text-xs text-[var(--mail-text-2)]">
                <FileArchive className="h-4 w-4" aria-hidden />
                {items.length} file{items.length === 1 ? "" : "s"} · {formatSize(total)}
                {locked ? (
                    <span className="inline-flex items-center gap-1 font-semibold">
                        <Lock className="h-3.5 w-3.5" aria-hidden /> Password-protected
                    </span>
                ) : null}
                <span className="ml-auto">Click a file to preview it</span>
            </div>
            <div className="min-h-0 flex-1 overflow-auto">
                {items.length === 0 ? (
                    <p className="p-6 text-center text-sm text-[var(--mail-text-2)]">This zip is empty.</p>
                ) : (
                    <table className="w-full border-collapse text-[13px]">
                        <thead className="sticky top-0 z-[1] bg-[var(--mail-pane)] text-left text-xs text-[var(--mail-text-2)]">
                            <tr>
                                <th scope="col" className="px-4 py-2 font-semibold">Name</th>
                                <th scope="col" className="w-28 px-4 py-2 text-right font-semibold">Size</th>
                                <th scope="col" className="hidden w-44 px-4 py-2 font-semibold sm:table-cell">Modified</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item) => (
                                <tr key={item.path} className="border-t border-[var(--mail-line)] hover:bg-[var(--mail-hover)]">
                                    <td className="max-w-0 px-4 py-1.5">
                                        <button
                                            type="button"
                                            onClick={() => onOpen(item)}
                                            title={`Open ${item.path}`}
                                            className="flex w-full min-w-0 items-center gap-2 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                        >
                                            <FileIcon className="h-4 w-4 shrink-0 text-[var(--mail-text-2)]" aria-hidden />
                                            <span className="min-w-0 truncate">
                                                {item.folder ? <span className="text-[var(--mail-text-3)]">{item.folder}/</span> : null}
                                                <span className="text-foreground hover:underline">{item.name}</span>
                                            </span>
                                            {item.encrypted ? <Lock className="h-3.5 w-3.5 shrink-0 text-[var(--mail-text-3)]" aria-label="Password-protected" /> : null}
                                        </button>
                                    </td>
                                    <td className="whitespace-nowrap px-4 py-1.5 text-right tabular-nums text-[var(--mail-text-2)]">{formatSize(item.size)}</td>
                                    <td className="hidden whitespace-nowrap px-4 py-1.5 tabular-nums text-[var(--mail-text-2)] sm:table-cell">
                                        {item.date ? item.date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : ""}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Plain text as a page; CSV / TSV as a table (first rows). */
function TextPreview({ fileName, text }: { fileName: string; text: string }) {
    const ext = extensionOf(fileName);
    const sep = ext === "csv" ? "," : ext === "tsv" ? "\t" : null;
    const rows = useMemo(() => (sep ? parseDelimited(text, sep, MAX_TABLE_ROWS + 1) : null), [text, sep]);

    if (rows && rows.length > 0) {
        const [head, ...body] = rows;
        const shown = body.slice(0, MAX_TABLE_ROWS - 1);
        return (
            <div className="absolute inset-0 overflow-auto bg-card">
                <table className="min-w-full border-collapse text-[13px] tabular-nums">
                    <thead className="sticky top-0 z-[1] bg-[var(--mail-pane)]">
                        <tr>
                            {(head ?? []).map((h, i) => (
                                <th key={i} className="whitespace-nowrap border border-[var(--mail-line)] px-2.5 py-1.5 text-left font-semibold">{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map((r, i) => (
                            <tr key={i} className="even:bg-[var(--mail-pane)]">
                                {r.map((c, j) => (
                                    <td key={j} className="max-w-[28rem] truncate whitespace-nowrap border border-[var(--mail-line)] px-2.5 py-1" title={c}>{c}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
                {rows.length > MAX_TABLE_ROWS ? (
                    <p className="p-3 text-xs text-[var(--mail-text-2)]">Showing the first {MAX_TABLE_ROWS.toLocaleString()} rows — download the file for the rest.</p>
                ) : null}
            </div>
        );
    }
    return (
        <pre className="absolute inset-0 overflow-auto whitespace-pre-wrap break-words bg-card p-5 font-mono text-[13px] leading-5 text-foreground">
            {text || "(empty file)"}
        </pre>
    );
}

/** Excel-like grid: column letters, row numbers, sticky headers, a tab per sheet. */
function SheetGrid({ sheets }: { sheets: SheetPreview[] }) {
    const [index, setIndex] = useState(0);
    const sheet = sheets[index] ?? sheets[0];
    const width = useMemo(() => Math.max(0, ...(sheet?.rows ?? []).map((r) => r.length)), [sheet]);
    if (!sheet || sheet.rows.length === 0) {
        return (
            <div className="absolute inset-0 flex items-center justify-center bg-card text-sm text-[var(--mail-text-2)]">
                {sheets.length === 0 ? "This workbook has no data to show." : `“${sheet?.name ?? ""}” is empty.`}
            </div>
        );
    }
    const head = "border border-[var(--mail-line)] bg-[var(--mail-pane)] px-2 py-1 text-[11px] font-normal text-[var(--mail-text-3)]";
    return (
        <div className="absolute inset-0 flex flex-col bg-card">
            <div className="min-h-0 flex-1 overflow-auto">
                <table className="border-collapse text-[13px] tabular-nums">
                    <thead className="sticky top-0 z-[2]">
                        <tr>
                            <th className={cn(head, "sticky left-0 z-[3] min-w-10")} aria-hidden />
                            {Array.from({ length: width }, (_, c) => (
                                <th key={c} scope="col" className={cn(head, "min-w-16 text-center")}>{columnName(c)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sheet.rows.map((r, i) => (
                            <tr key={i}>
                                <th scope="row" className={cn(head, "sticky left-0 z-[1] text-right")}>{i + 1}</th>
                                {Array.from({ length: width }, (_, c) => (
                                    <td
                                        key={c}
                                        title={r[c] || undefined}
                                        className="max-w-[24rem] truncate whitespace-nowrap border border-[var(--mail-line)] px-2 py-1 text-foreground"
                                    >
                                        {r[c] ?? ""}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
                {sheet.truncated ? (
                    <p className="p-3 text-xs text-[var(--mail-text-2)]">Showing the first 1,000 rows and 60 columns — download the file for the rest.</p>
                ) : null}
            </div>
            {sheets.length > 1 ? (
                <div role="tablist" aria-label="Sheets" className="flex shrink-0 gap-0.5 overflow-x-auto border-t border-[var(--mail-line)] bg-[var(--mail-pane)] px-2 py-1">
                    {sheets.map((s, i) => (
                        <button
                            key={`${s.name}-${i}`}
                            type="button"
                            role="tab"
                            aria-selected={i === index}
                            onClick={() => setIndex(i)}
                            className={cn(
                                "whitespace-nowrap rounded px-3 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                i === index
                                    ? "bg-card font-semibold text-foreground shadow-[var(--mail-shadow-4)]"
                                    : "text-[var(--mail-text-2)] hover:bg-[var(--mail-hover)]"
                            )}
                        >
                            {s.name}
                        </button>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

/** 0 → A, 25 → Z, 26 → AA (Excel column letters). */
function columnName(i: number): string {
    let n = i + 1;
    let s = "";
    while (n > 0) {
        const m = (n - 1) % 26;
        s = String.fromCharCode(65 + m) + s;
        n = Math.floor((n - 1) / 26);
    }
    return s;
}

/** The sheets as a plain HTML document — what Print and "open in a tab" show. Every cell is escaped. */
function sheetsToHtml(title: string, sheets: SheetPreview[]): string {
    const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    const body = sheets
        .map((s) => `<h2>${esc(s.name)}</h2><table>${s.rows.map((r) => `<tr>${r.map((c) => `<td>${esc(c)}</td>`).join("")}</tr>`).join("")}</table>`)
        .join("");
    return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>`
        + `<style>body{font:12px system-ui,sans-serif;margin:16px}table{border-collapse:collapse;margin-bottom:24px}`
        + `td{border:1px solid #ccc;padding:2px 6px;white-space:nowrap}h2{font-size:14px;margin:0 0 8px}</style>`
        + `</head><body>${body}</body></html>`;
}

/** CSV / TSV with quoted fields ("a, b" and "" escapes); stops after maxRows. */
function parseDelimited(text: string, sep: string, maxRows: number): string[][] {
    const rows: string[][] = [];
    let row: string[] = [];
    let cell = "";
    let quoted = false;
    for (let i = 0; i < text.length; i++) {
        const c = text.charAt(i);
        if (quoted) {
            if (c === '"') {
                if (text.charAt(i + 1) === '"') {
                    cell += '"';
                    i++;
                } else quoted = false;
            } else cell += c;
            continue;
        }
        if (c === '"' && cell === "") quoted = true;
        else if (c === sep) {
            row.push(cell);
            cell = "";
        } else if (c === "\n" || c === "\r") {
            if (c === "\r" && text.charAt(i + 1) === "\n") i++;
            row.push(cell);
            rows.push(row);
            row = [];
            cell = "";
            if (rows.length >= maxRows) return rows;
        } else cell += c;
    }
    if (cell !== "" || row.length > 0) {
        row.push(cell);
        rows.push(row);
    }
    return rows;
}

function HeaderAction({
    icon, label, disabled, onClick
}: {
    icon: React.ReactNode;
    label: string;
    disabled?: boolean;
    onClick: () => void;
}) {
    return (
        <Button variant="ghost" size="sm" className="h-8 shrink-0 gap-1.5 px-2 font-normal" onClick={onClick} disabled={disabled} title={label}>
            {icon}
            <span className="hidden sm:inline">{label}</span>
        </Button>
    );
}
